import type { Transition, Variants } from 'motion/react'

export type SlideDirection = 'forward' | 'back'

export const motionEaseOut = [0.23, 1, 0.32, 1] as const
export const motionEaseInOut = [0.77, 0, 0.175, 1] as const

export const panelEnterTransition: Transition = {
  duration: 0.18,
  ease: motionEaseOut,
}

export const panelExitTransition: Transition = {
  duration: 0.12,
  ease: motionEaseOut,
}

/**
 * Direction-aware variants for occasional panel transitions.
 *
 * The short travel distance preserves spatial context without making product
 * navigation feel like a carousel. Because these variants use a combined
 * transform string, consumers must explicitly select fadeVariants for reduced
 * motion and instantVariants for keyboard-initiated changes.
 * Pass direction as custom prop: custom="forward" or custom="back".
 */
export const slideVariants: Variants = {
  initial: (direction: SlideDirection) => ({
    transform: `translateX(${direction === 'forward' ? '16px' : '-16px'})`,
    opacity: 0,
  }),
  animate: {
    transform: 'translateX(0px)',
    opacity: 1,
    transition: panelEnterTransition,
  },
  exit: (direction: SlideDirection) => ({
    transform: `translateX(${direction === 'forward' ? '-12px' : '12px'})`,
    opacity: 0,
    transition: panelExitTransition,
  }),
}

/**
 * Fade animation variants for simpler transitions.
 */
export const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: panelEnterTransition,
  },
  exit: {
    opacity: 0,
    transition: panelExitTransition,
  },
}

/**
 * Immediate state swap for keyboard-initiated navigation.
 */
export const instantVariants: Variants = {
  initial: { opacity: 1, transform: 'translateX(0px)' },
  animate: {
    opacity: 1,
    transform: 'translateX(0px)',
    transition: { duration: 0 },
  },
  exit: {
    opacity: 1,
    transform: 'translateX(0px)',
    transition: { duration: 0 },
  },
}

/**
 * Height collapse/expand (cards, disclosure strips). A touch longer than the
 * panel swap because it moves more pixels; the exit is faster than the enter
 * so closing never makes the user wait.
 */
export const collapseEnterTransition: Transition = {
  duration: 0.2,
  ease: motionEaseOut,
}

export const collapseExitTransition: Transition = {
  duration: 0.14,
  ease: motionEaseOut,
}

/**
 * Tailwind classes for a disclosure chevron that rotates with the same
 * duration and curve as {@link collapseEnterTransition}. Reduced motion
 * snaps it instead.
 */
export const collapseChevronTransitionClass =
  'transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none'
